/**
 * Account `/account` — the signed-in user's profile settings: handle (validated
 * inline with the SAME shared rules the server enforces), bio, the avatar
 * picker, and a compact badge summary (earned count linking to their public
 * wall). Saving goes through the ONE trusted writer (`updateMyProfile`
 * callable — direct client updates were revoked in migration 0022); a handle
 * conflict comes back as an inline error.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HANDLE_MAX_LEN,
  HANDLE_MIN_LEN,
  canonicalHandle,
  isValidHandle,
} from '@djgg/creations';
import { useAuth, useMyProfile, useServices } from '../services/context.js';
import { useUserBadges } from '../services/useUserBadges.js';
import { AvatarPicker } from '../components/AvatarPicker.js';
import { BADGES } from '../components/badgeManifest.js';
import { UserAvatar } from '../components/UserAvatar.js';

/** Inline mirror of the server's handle rule ('' = fine). */
export function handleError(raw: string): string {
  const handle = canonicalHandle(raw);
  if (isValidHandle(handle)) return '';
  return `Handle must be ${HANDLE_MIN_LEN}–${HANDLE_MAX_LEN} characters: a–z, 0–9, - or _, starting with a letter or digit.`;
}

export function Account() {
  const services = useServices();
  const { user } = useAuth();
  const { loading, profile } = useMyProfile();
  // Earned count for the summary line — shares the App-level MyBadgesProvider
  // subscription (this is always MY uid on a guarded route).
  const earned = useUserBadges(user && !user.isAnonymous ? user.uid : undefined);

  const [handle, setHandle] = useState('');
  const [bio, setBio] = useState('');
  const [avatarKey, setAvatarKey] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Seed the form ONCE, when the profile first appears in the directory —
  // later live updates must not clobber in-progress edits. A user without a
  // profile row (bootstrap stub never landed) starts from an empty form; the
  // callable (re)creates the row on save.
  useEffect(() => {
    if (seeded || !profile) return;
    setHandle(profile.handle);
    setBio(profile.bio);
    setAvatarKey(profile.avatarKey);
    setSeeded(true);
  }, [seeded, profile]);

  if (loading) {
    return <div className="empty">Loading your profile…</div>;
  }

  const validation = handleError(handle);

  async function save(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (busy || validation) return;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await services.callable.updateMyProfile({
        handle: canonicalHandle(handle),
        bio: bio.trim(),
        avatarKey,
      });
      setHandle(canonicalHandle(handle));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your profile.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container" data-testid="account-page">
      <h1 className="gallery__title" style={{ margin: '24px 0 16px' }}>
        Account settings
      </h1>
      <div className="panel" style={{ maxWidth: 640 }}>
        <div className="row" style={{ gap: 12, marginBottom: 16 }}>
          <UserAvatar avatarKey={avatarKey} size={56} />
          <div>
            <div style={{ fontWeight: 700 }}>{handle ? `@${canonicalHandle(handle)}` : 'Unnamed pigeon'}</div>
            {profile?.claimedAt ? null : (
              <div className="muted" style={{ fontSize: 12 }}>
                Pick a handle and an avatar to claim your profile.
              </div>
            )}
          </div>
        </div>
        <div className="muted account__badges" data-testid="account-badges">
          Badges: {earned.size} / {BADGES.length} earned
          {profile?.handle && (
            <>
              {' · '}
              <Link to={`/u/${profile.handle}`} data-testid="account-badges-link">
                see them on your page
              </Link>
            </>
          )}
        </div>
        <form onSubmit={(e) => void save(e)}>
          <div className="field">
            <label className="field__label" htmlFor="account-handle">
              Handle
            </label>
            <input
              id="account-handle"
              className="input"
              value={handle}
              maxLength={HANDLE_MAX_LEN}
              onChange={(e) => {
                setHandle(e.target.value);
                setSaved(false);
              }}
              data-testid="account-handle"
            />
            {handle && validation && (
              <div className="error-text" data-testid="account-handle-error">
                {validation}
              </div>
            )}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="account-bio">
              Bio
            </label>
            <textarea
              id="account-bio"
              className="textarea"
              rows={3}
              maxLength={500}
              value={bio}
              onChange={(e) => {
                setBio(e.target.value);
                setSaved(false);
              }}
              placeholder="Tell the flock about yourself…"
              data-testid="account-bio"
            />
          </div>
          <div className="field">
            <span className="field__label">Avatar</span>
            <AvatarPicker value={avatarKey} onChange={(id) => { setAvatarKey(id); setSaved(false); }} disabled={busy} />
          </div>
          {error && (
            <div className="error-text" data-testid="account-error">
              {error}
            </div>
          )}
          {saved && (
            <div className="muted" data-testid="account-saved">
              Profile saved.
            </div>
          )}
          <button
            className="btn btn--primary"
            type="submit"
            disabled={busy || Boolean(validation)}
            data-testid="account-save"
          >
            {busy ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </div>
    </div>
  );
}
